import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

export default function Shop() {
  const [sp, setSp] = useSearchParams();
  const cat = sp.get("cat") || "";
  const q = sp.get("q") || "";
  const sort = sp.get("sort") || "price_desc";
  const set = (k, v) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); setSp(n, { replace: true }); };
  const [text, setText] = useState(q);
  useEffect(() => {
    const t = setTimeout(() => { if (text !== q) set("q", text); }, 400);
    return () => clearTimeout(t);
  }, [text]);

  const { data: cats = [] } = useQuery({ queryKey: ["cats"], queryFn: () => api.get("/categories").then((r) => r.data) });
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["shop", cat, q, sort],
    queryFn: () => api.get("/products", { params: { category: cat, q, sort, limit: 200 } }).then((r) => r.data),
  });

  return (
    <div className="max-w-[1500px] mx-auto px-5 md:px-10 pt-16" data-testid="shop-page">
      <div className="overline-label mb-4">// the arsenal</div>
      <h1 className="font-display text-6xl md:text-8xl leading-[0.9] chrome-text">{cat ? cats.find((c) => c.slug === cat)?.name || "Arsenal" : "All targets"}</h1>

      <div className="mt-12 flex flex-col lg:flex-row gap-4 lg:items-center justify-between sticky top-[68px] z-30 bg-[#050505]/90 backdrop-blur-xl py-4 border-b border-white/10">
        <div className="flex flex-wrap gap-2" data-testid="shop-category-filters">
          <button onClick={() => set("cat", "")} className={`px-4 py-2 text-xs font-bold tracking-[0.18em] uppercase border transition-colors ${!cat ? "bg-[#e10032] border-[#e10032]" : "border-white/20 text-[#bbb] hover:border-[#ff2a55]"}`} data-testid="filter-all">All</button>
          {cats.map((c) => (
            <button key={c.id} onClick={() => set("cat", c.slug)} className={`px-4 py-2 text-xs font-bold tracking-[0.18em] uppercase border transition-colors ${cat === c.slug ? "bg-[#e10032] border-[#e10032]" : "border-white/20 text-[#bbb] hover:border-[#ff2a55]"}`} data-testid={`filter-${c.slug}`}>
              {c.name} <span className="opacity-60">{c.count}</span>
            </button>
          ))}
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#777]" />
            <input className="field !pl-10 w-56" placeholder="Search names" value={text} onChange={(e) => setText(e.target.value)} data-testid="shop-search-input" />
          </div>
          <select className="field !w-auto" value={sort} onChange={(e) => set("sort", e.target.value)} data-testid="shop-sort-select">
            <option value="price_desc">Price: high to low</option>
            <option value="price_asc">Price: low to high</option>
            <option value="new">Newest</option>
            <option value="featured">Featured</option>
          </select>
        </div>
      </div>

      <div className="py-4 text-xs tracking-[0.2em] uppercase text-[#777]" data-testid="shop-count">{isLoading ? "Scanning..." : `${items.length} targets`}</div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 pb-10" data-testid="shop-grid">
        {items.map((p, i) => <ProductCard key={p.id} p={p} index={i} />)}
      </div>
      {!isLoading && items.length === 0 && <div className="py-24 text-center text-[#777]" data-testid="shop-empty">Nothing on radar. Try another filter.</div>}
    </div>
  );
}
